import { useOrdersStore } from "./orders.store";
import { Workflow, WorkflowStage } from "../interfaces/WorkflowInterface";
import { getWorkflowsWithStages } from "../services/WorkflowsService";
//import { getSingleWorkflow } from "../services/WorkflowsService";
import { getSingleWorkflowStage } from "../services/WorkflowStagesService";
import { OrderItem } from "@/types";
import { isEmpty } from "lodash";
import { defineStore } from "pinia";
import { diff } from "json-diff-ts";

export type RootState = {
  stagesWithOrders: Map<number, OrderItem[]>;
  selectedWorkflow: Workflow;
  workflows: Workflow[];
  isEditing: Map<string | number, boolean>;
  originalWorkflows: string;
  selectedStageId: number;
};

export const STAGE_TYPES = {
  UNSTARTED: "Unstarted",
  STARTED: "Started",
  DONE: "Done",
  NULL: "Undefined",
  ARCHIVED: "Archived",
};

export const STAGE_CATEGORIES = ["Generic", "Partners", "Printers"];

export const sortStages = (stages: WorkflowStage[]) => {
  const typeOrder = Object.values(STAGE_TYPES);
  return [...stages].sort((a, b) => {
    const typeA = a.type || STAGE_TYPES.NULL;
    const typeB = b.type || STAGE_TYPES.NULL;
    const indexA = typeOrder.indexOf(typeA);
    const indexB = typeOrder.indexOf(typeB);

    if (indexA !== indexB) {
      return indexA - indexB;
    }
    return a.position - b.position;
  });
};

import { useLocalStorage } from "@vueuse/core";

export const useWorkflowsStore = defineStore("workflows", {
  state: (): RootState => ({
    stagesWithOrders: new Map<number, OrderItem[]>(),
    selectedWorkflow: {} as Workflow,
    workflows: [],
    isEditing: new Map<string | number, boolean>(),
    originalWorkflows: "[]",
    selectedStageId: useLocalStorage<number>("selectedStageId", 0, {
      serializer: { read: (value: string) => Number(value) || 0, write: (value: number) => String(value) },
    }) as unknown as number,
  }),
  getters: {
    workflowStages: (state) => {
      return state.workflows.map((workflow: Workflow) => workflow.workflowStages).flat();
    },
    hasChanges(): boolean {
      const differences = diff(this.workflows, JSON.parse(this.originalWorkflows));
      console.log(differences);
      return differences.length > 0;
    },
  },
  actions: {
    async fetchOrdersForStage(stageId: number): Promise<OrderItem[]> {
      if (!stageId) return [];
      const stage: WorkflowStage = await getSingleWorkflowStage(stageId);
      if (stage.id && stage.orderItems) {
        this.workflows.map((workflow: Workflow) => {
          if (workflow.workflowStages) {
            const foundStage = workflow.workflowStages?.find((s: WorkflowStage) => s.id === stage.id);
            if (foundStage) foundStage.orderItems = stage.orderItems;
          }
          return workflow;
        });
      }
      return stage.orderItems || [];
    },

    async fetchAllWorkflows(workflows?: Workflow[]) {
      const res = workflows || (await getWorkflowsWithStages());
      if (res) {
        this.workflows = res.map((workflow: Workflow) => {
          if (workflow.workflowStages) {
            workflow.workflowStages = sortStages(workflow.workflowStages);
            if (workflow.defaultStageId) {
              workflow.defaultStage = workflow.workflowStages.find((s: WorkflowStage) => s.id === workflow.defaultStageId);
            }
          }
          return workflow;
        });
      }

      this.originalWorkflows = JSON.stringify(this.workflows);
      if (!isEmpty(this.workflows[0])) {
        if (isEmpty(this.selectedWorkflow)) this.selectedWorkflow = this.workflows[0];
        else {
          const workflow: Workflow | undefined = this.workflows.find((workflow: Workflow) => workflow.id === this.selectedWorkflow.id);
          if (workflow) this.selectedWorkflow = workflow;
        }
      }
      const ordersStore = useOrdersStore();
      this.workflows.forEach((workflow: Workflow) => {
        if (!ordersStore.selectedSortingMethod.some((sm) => sm.workflowId === workflow.id)) {
          ordersStore.selectedSortingMethod.push({ workflowId: workflow.id, value: "createdAt", label: "Creation Date" });
        }
        if (!ordersStore.selectedSortingOrder.some((so) => so.workflowId === workflow.id)) {
          ordersStore.selectedSortingOrder.push({ workflowId: workflow.id, order: "DESC" });
        }
      });
    },

    fetchOrdersForAllStages() {
      this.selectedWorkflow.workflowStages?.forEach((stage: WorkflowStage) => {
        if (stage.id) this.fetchOrdersForStage(stage.id);
      });
    },
    addStageToWorkflow(type: "Unstarted" | "Started" | "Done" | "Undefined" | null) {
      const positions = this.selectedWorkflow.workflowStages?.filter((stage: WorkflowStage) => stage.type === type).map((stage: WorkflowStage) => stage.position);
      const position = positions?.length ? Math.max(...positions) + 1 : 1;

      //increment all positions greater than or equal to the new position
      this.selectedWorkflow.workflowStages = this.selectedWorkflow.workflowStages?.map((stage: WorkflowStage) => {
        if (stage.position >= position) {
          stage.position = stage.position + 1;
        }
        return stage;
      });

      this.selectedWorkflow.workflowStages?.push({
        id: 0,
        name: "Stage " + position,
        description: `New ${type} stage`,
        position: position,
        type: type,
        color: "ccc5b9",
        category: "Generic",
      });

      if (this.selectedWorkflow.workflowStages) {
        this.selectedWorkflow.workflowStages = sortStages(this.selectedWorkflow.workflowStages);
      }

      const editingString = this.selectedWorkflow.id !== undefined ? `${this.selectedWorkflow.id}0${position}` : `9990${position}`;

      this.isEditing.set(`${editingString}`, true);
    },

    resetToLastSaved() {
      this.workflows = JSON.parse(this.originalWorkflows);
    },
  },
});
